import type { Attachment } from '../../../../store/previewer/types'
import type {
  CheckRun,
  CheckRunGeneralInformationWrapperProps,
  Claim,
  FileGroup,
  GroupSetting,
  Stat
} from '../types'
import { PaymentMethodDisplayType } from '../types'

import {
  CaretDownFilled,
  CaretDownOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  CopyOutlined,
  DollarCircleOutlined,
  FileExcelFilled,
  FilePdfFilled,
  FileTextFilled,
  SearchOutlined,
  SettingOutlined,
  WarningFilled
} from '@ant-design/icons'
import { Alert, App as AntdApp, Button, Select, Skeleton, Tooltip } from 'antd'
import cx from 'classnames'
import { format, isSameDay, parseISO } from 'date-fns'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import $api, { API_URL } from '../../../../API'
import { useAppDispatch, useAppSelector } from '../../../../store'
import * as previewActions from '../../../../store/previewer'
import { AVAILABLE_FILE_TYPES_MAPPER, paymentMethods, reportFileTypeMapper } from '../constants'
import { currencyFormatter, round, uniquePredicate } from '../functions'

import { Role } from '../../../../domain/role.enum'
import { Card } from './card'
import CheckRegisters from './check-register'
import { Timeline } from './timeline'
import { Totals } from './totals'
import { useQuery } from '@tanstack/react-query'
import { NomiStatusFileTooltip } from './nomi-status-file-tooltip'
import { Duplicate } from '../../../../components/Icons/Duplicate/Duplicate'

type QueryKeyWithParams<T> = T extends Array<string | infer U> ? [string, U] : never

export const CheckRunGeneralInformationWrapper = ({
  checkRunId,
  groupId
}: CheckRunGeneralInformationWrapperProps): JSX.Element => {
  const navigate = useNavigate()
  const { notification } = AntdApp.useApp()

  const query = useQuery({
    queryKey: ['check-run-totals', { checkRunId: checkRunId, groupId: groupId }],

    async queryFn({ queryKey, signal }) {
      const [, { checkRunId, groupId }] = queryKey as QueryKeyWithParams<typeof queryKey>

      const response = await $api.get<CheckRun[]>(
        `/check-runs/${checkRunId}/groups/${groupId}/totals`,
        {
          signal
        }
      )

      return response.data[0]
    }
  })

  const user = useAppSelector(state => state.app.user)

  const [mode, setMode] = useState<'general' | 'timeline' | 'totals'>('general')
  const [displayAllFiles, setDisplayAllFiles] = useState(false)
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false)
  const [showTemporalCopiedTooltip, setShowTemporalCopiedTooltip] = useState(false)

  const [showSettings, setShowSettings] = useState(false)
  const [paymentMethodDisplayType, setPaymentMethodDisplayType] =
    useState<PaymentMethodDisplayType>(PaymentMethodDisplayType.Auto)

  const dispatch = useAppDispatch()

  const checkRun = query.data
  const hasPaymentFiles = (checkRun?.payment_files?.length ?? 0) > 0

  const duplicateClaims = useMemo(() => {
    if (!checkRun?.payment_files) {
      return []
    }

    const allClaims = (checkRun.payment_files ?? []).flatMap(file =>
      (file.eobs ?? []).flatMap(eob => eob.claims ?? [])
    )

    const uniqueClaims: Claim[] = []
    const seenClaimIds = new Set<string>()

    for (const claim of allClaims) {
      if (claim.has_multiple_patients && !seenClaimIds.has(claim.id)) {
        uniqueClaims.push(claim)
        seenClaimIds.add(claim.id)
      }
    }

    return uniqueClaims
  }, [checkRun?.payment_files])

  const handlePdfPreviewClick = (file: Attachment) => (): void => {
    dispatch(previewActions.open(file))
  }

  const handleCopyCheckRunId = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(checkRunId)

      setShowCopiedTooltip(true)

      setTimeout(() => {
        setShowCopiedTooltip(false)
      }, 2000)
    } catch {
      notification.error({
        message: 'Failed to copy Check Run ID',
        showProgress: true,
        pauseOnHover: true
      })
    }
  }

  const handleCopyTemporalId = async (): Promise<void> => {
    try {
      if (checkRun?.temporal_workflow_id) {
        await navigator.clipboard.writeText(checkRun.temporal_workflow_id)

        setShowTemporalCopiedTooltip(true)

        setTimeout(() => {
          setShowTemporalCopiedTooltip(false)
        }, 2000)
      }
    } catch {
      notification.error({
        message: 'Failed to copy Temporal Workflow ID',
        showProgress: true,
        pauseOnHover: true
      })
    }
  }

  const handleTemporalNavigate = (): void => {
    if (checkRun?.temporal_workflow_id) {
      navigate(`/diagnostic/temporal?workflowId=${checkRun.temporal_workflow_id}`)
    }
  }

  const hasAccess = (roles: Role[]): boolean => {
    return roles.some(role => user.roles.includes(role))
  }

  const settings: GroupSetting[] = [
    {
      label: 'Zelis eligible',
      value:
        typeof checkRun?.group?.zelis_eligible === 'boolean'
          ? checkRun.group.zelis_eligible
            ? 'Yes'
            : 'No'
          : undefined
    },
    {
      label: 'Checks only',
      value:
        typeof checkRun?.group?.checks_only === 'boolean'
          ? checkRun.group.checks_only
            ? 'Yes'
            : 'No'
          : undefined
    },
    {
      label: 'Activation date',
      value:
        typeof checkRun?.group?.activation_date === 'string'
          ? format(parseISO(checkRun.group.activation_date), 'MM/dd/yyyy')
          : undefined
    }
  ].filter(setting => setting.value !== undefined)

  const totalsByPaymentMethodFromClaims: Record<string, number> = (checkRun?.payment_files ?? [])
    .flatMap(file => file.eobs)
    .map(eob => eob.payment_method)
    .sort((a, b) => a.localeCompare(b))
    .filter(uniquePredicate)
    .reduce((acc, method) => {
      let amount = (checkRun?.payment_files ?? [])
        .flatMap(file => file.eobs)
        .filter(eob => eob.payment_method === method)
        .flatMap(eob => eob.claims)
        .filter(claim => {
          if (method === 'Network') {
            return claim.is_eligible
          }

          return true
        })
        .reduce((acc, claim) => acc + claim.amount, 0)

      if (method === 'Check') {
        const voidedAmount =
          (checkRun?.voided_checks ?? []).reduce((acc, check) => acc + check.amount, 0) ?? 0
        amount -= voidedAmount
      }

      return {
        ...acc,
        [method]: round(amount)
      }
    }, {})

  const networkEobs = (checkRun?.payment_files ?? []).flatMap(pf =>
    pf.eobs.filter(e => e.payment_method === 'Network')
  )

  const collectZelisClaims = (checkRun?.payment_files ?? [])
    .flatMap(pf =>
      pf.eobs
        .filter(e => ['Epay', 'Check', 'ACH'].includes(e.payment_method))
        .flatMap(e => e.claims)
    )
    .filter(claim => claim.is_eligible)

  const lcrTotal =
    typeof checkRun?.lcr_file?.total === 'number' ? checkRun?.lcr_file.total : undefined

  const luminxTotal = round(
    (checkRun?.payment_files?.length ?? 0) > 0
      ? (checkRun?.payment_files ?? []).reduce((acc, file) => acc + file.file_total, 0)
      : null
  )
  const zelisTotal = round(
    collectZelisClaims.length > 0
      ? collectZelisClaims.reduce((acc, file) => acc + file.amount, 0)
      : null
  )
  const networkTotal = round(
    networkEobs.length > 0 ? networkEobs.reduce((acc, eob) => acc + eob.check_amount, 0) : null
  )
  const lucentpmt =
    zelisTotal !== null || networkTotal !== null
      ? round((zelisTotal ?? 0) + (networkTotal ?? 0))
      : null

  const lastReport = checkRun?.check_register_reports[0]

  const methods = new Set(
    (checkRun?.payment_files ?? []).flatMap(file => file.eobs).map(eob => eob.payment_method)
  )

  const statColumns: Stat[] = paymentMethods.flatMap(method => {
    const lastReportTotalMapper: Record<string, string> = {
      Check: 'checks',
      Epay: 'epay',
      ACH: 'ach',
      Network: 'network'
    }

    const lrt = lastReport?.totals?.[lastReportTotalMapper[method.value] ?? '']

    const amountFromClaims = totalsByPaymentMethodFromClaims[method.value] ?? 0
    const amountFromLastReport = lrt?.payments.amount ?? 0

    const amount =
      paymentMethodDisplayType === PaymentMethodDisplayType.Auto
        ? lrt
          ? amountFromLastReport
          : amountFromClaims
        : paymentMethodDisplayType === PaymentMethodDisplayType.LastReport
          ? amountFromLastReport
          : amountFromClaims

    const voidedAmountFromClaims =
      method.value === 'Check'
        ? round((checkRun?.voided_checks ?? []).reduce((acc, check) => acc + check.amount, 0) ?? 0)
        : 0

    const voidedAmountFromLastReport = -1 * (lrt?.voids.amount ?? 0)

    const voidedAmount =
      paymentMethodDisplayType === PaymentMethodDisplayType.Auto
        ? lrt
          ? voidedAmountFromLastReport
          : voidedAmountFromClaims
        : paymentMethodDisplayType === PaymentMethodDisplayType.LastReport
          ? voidedAmountFromLastReport
          : voidedAmountFromClaims

    return [
      {
        label: `${method.label} Net`,
        amount,
        tooltip:
          amount + voidedAmount !== 0 ? (
            <span>
              {method.label} Total: {currencyFormatter.format(amount + voidedAmount)}
            </span>
          ) : undefined,

        isEmpty:
          amount === 0 && voidedAmount === 0 && !methods.has(method.value) && !hasPaymentFiles,

        span: 2
      },
      {
        label: `${method.label} Voided`,
        amount: voidedAmount,
        tooltip:
          amount + voidedAmount !== 0 ? (
            <span>
              {method.label} Total: {currencyFormatter.format(amount + voidedAmount)}
            </span>
          ) : undefined,

        isEmpty:
          amount === 0 && voidedAmount === 0 && !methods.has(method.value) && !hasPaymentFiles,

        span: 2
      }
    ]
  })

  const zelisColumn = statColumns.filter(
    s => s.label !== 'Network Net' && s.label !== 'Network Voided'
  )
  const networkColumn = statColumns.filter(
    s => s.label === 'Network Net' || s.label === 'Network Voided'
  )

  const token = window.localStorage.getItem('token')

  const relevantReports = (checkRun?.check_register_reports ?? []).some(r => !!r.set)
    ? (() => {
        const latestSet =
          (checkRun?.check_register_reports ?? [])
            .map(r => r.set)
            .filter(Boolean)
            .sort(
              (a, b) => parseISO(b.created_at).getTime() - parseISO(a.created_at).getTime()
            )[0] ?? undefined

        if (!latestSet) {
          return checkRun?.check_register_reports
        }

        return (checkRun?.check_register_reports ?? []).filter(r => r.set?.id === latestSet.id)
      })()
    : checkRun?.check_register_reports

  const allFileGroups: FileGroup[] = [
    {
      name: 'LCR PDF',
      files: [
        ...(typeof checkRun?.lcr_file?.original_filename === 'string'
          ? [
              {
                type: 'lcr-file',
                name: checkRun.lcr_file.original_filename,
                href: `${API_URL}/files/${encodeURIComponent(checkRun.lcr_file.original_filename)}?event-type-id=1&file-type-id=6&token=${encodeURIComponent(token ?? '')}`,
                priority: 2,
                icon: {
                  component: FilePdfFilled,
                  className: 'text-lucent-red'
                }
              }
            ]
          : [])
      ]
    },
    {
      name: 'CR Reports',
      files: (relevantReports ?? [])
        .sort((a, b) => {
          if (a.name.includes('network')) {
            return 1
          }
          if (b.name.includes('network')) {
            return -1
          }
          return a.name.localeCompare(b.name)
        })
        .map((report, _, reports) => {
          const otherReportsWithSameName = reports.filter(
            r => r.name === report.name && r.id !== report.id
          )

          const hasOtherReportsWithSameName = otherReportsWithSameName.length > 0
          const isMostRecentAmongOthers = otherReportsWithSameName.every(
            r => parseISO(r.created_at) < parseISO(report.created_at)
          )
          void isMostRecentAmongOthers

          return {
            type: 'cr-report',
            name: hasOtherReportsWithSameName ? (
              <span>
                <span className='p-1 -m-1 mr-1 font-mono text-xs bg-gray-100 rounded'>
                  {format(
                    parseISO(report.created_at),
                    isSameDay(new Date(), parseISO(report.created_at))
                      ? 'HH:mm'
                      : 'MM/dd/yyyy HH:mm'
                  )}
                </span>

                <span>{report.name}</span>
              </span>
            ) : (
              report.name
            ),
            href: `${API_URL}/files/${encodeURIComponent(report.name)}?event-type-id=34&file-type-id=${reportFileTypeMapper[report.type]}&token=${encodeURIComponent(token ?? '')}&report-id=${encodeURIComponent(report.id)}`,
            priority:
              (report.type === 'detail' &&
                (report.options?.includePHI ?? false) === true &&
                (report.options?.includeDiagnosisDetails ?? false) === false) ||
              (report.type === 'summary' && !report.options)
                ? 1
                : undefined,

            icon: {
              component: FileExcelFilled,
              className: 'text-lucent-green'
            },
            props: report
          }
        })
    },
    {
      name: 'Epay',
      files: (checkRun?.payment_files ?? [])
        .flatMap(file => file.events.received_from_zelis)
        .map(event => ({
          type: 'received-from-zelis',
          name: event.filename,
          href: `${API_URL}/files/${encodeURIComponent(event.filename)}?event-type-id=3&file-type-id=3&token=${encodeURIComponent(token ?? '')}`,
          icon: {
            component: FileTextFilled,
            className: 'text-lucent-primary'
          }
        }))
    },
    {
      name: 'Prepass',
      files: (checkRun?.payment_files ?? [])
        .flatMap(file => file.events.sent_to_zelis)
        .map(event => ({
          type: 'sent-to-zelis',
          name: event.filename,
          href: `${API_URL}/files/${encodeURIComponent(event.filename)}?event-type-id=2&file-type-id=2&token=${encodeURIComponent(token ?? '')}`,
          icon: {
            component: FileTextFilled,
            className: 'text-lucent-primary'
          }
        }))
    },
    {
      name: 'Payment Files',
      files:
        (checkRun?.payment_files ?? [])
          ?.map(file => ({
            type: 'payment-file',
            name: file.filename,
            href: `${API_URL}/files/${encodeURIComponent(file.filename)}?event-type-id=1&file-type-id=1&token=${encodeURIComponent(token ?? '')}`,
            icon: {
              component: FileTextFilled,
              className: 'text-lucent-primary'
            }
          }))
          ?.sort((a, b) => a.name.localeCompare(b.name)) ?? []
    },
    {
      name: 'Scheduled File',
      files: (checkRun?.scheduled_files ?? []).map(file => ({
        type: 'scheduled-file',
        name: file.name,
        href: `${API_URL}/files/${encodeURIComponent(file.name)}?event-type-id=200&file-type-id=200&token=${encodeURIComponent(token ?? '')}`,
        icon: {
          component: FileTextFilled,
          className: 'text-lucent-primary'
        }
      }))
    },
    {
      name: 'NOMI 835',
      files:
        (checkRun?.nomi835_files ?? []).map(file => ({
          type: 'nomi835',
          name: file.name,
          href: `${API_URL}/files/from-path/${encodeURIComponent(file.path)}?event-type-id=1&file-type-id=6&token=${encodeURIComponent(token ?? '')}`,
          priority: 0,
          icon: {
            component: FileTextFilled,
            className: 'text-lucent-primary'
          }
        })) ?? []
    },
    {
      name: 'NOMI Status Report',
      files:
        (checkRun?.nomi_status_files ?? []).map(file => {
          // Handle both new format (source_files_details) and old format (source, status, exception_type)
          const sourceFilesDetails =
            file.source_files_details ||
            (file.source
              ? [
                  {
                    source_file_name: file.source,
                    status: file.status || '',
                    exception_type: file.exception_type || '',
                    created_at: file.created_at
                  }
                ]
              : [])

          return {
            type: 'nomi_status_file',
            name: `${file.name} (${sourceFilesDetails.length} files)`,
            sourceFilesDetails,
            href: `${API_URL}/files/from-path/${encodeURIComponent(file.path)}?event-type-id=1&file-type-id=6&token=${encodeURIComponent(token ?? '')}`,
            priority: 0,
            icon: {
              component: FileTextFilled,
              className: 'text-lucent-primary'
            }
          }
        }) ?? []
    }
  ].filter(g => g.files.length > 0)

  const importantFileGroups = allFileGroups
    .map(({ files, ...group }) => ({
      ...group,
      files: files.map(file => ({
        ...file,
        group
      }))
    }))
    .flatMap(group => group.files)
    .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))
    .slice(0, 5)
    .reduce<FileGroup[]>((acc, entry) => {
      const { group, ...file } = entry
      let existing = acc.find(g => g.name === group.name)

      if (!existing) {
        existing = {
          ...group,
          files: []
        }

        acc = [...acc, existing]
      }

      existing.files = [...existing.files, file]

      return acc
    }, [])

  const canDisplayAllFiles =
    allFileGroups.flatMap(group => group.files).length >
    importantFileGroups.flatMap(group => group.files).length
  const fileGroups = displayAllFiles ? allFileGroups : importantFileGroups

  useEffect(() => {
    if (checkRun?.warning && checkRun?.report_totals) {
      setMode('totals')
    }
  }, [checkRun?.warning, checkRun?.report_totals])

  const lucentLCRDiff =
    typeof lcrTotal === 'number' && lucentpmt !== lcrTotal
      ? round(lcrTotal - (lucentpmt ?? 0))
      : null

  return (
    <div className='flex flex-col !font-normal !font-lato !text-xs !bg-lucent-background'>
      <div className='flex relative flex-row gap-4 justify-start items-start px-5 py-4 w-full'>
        <div className='flex absolute top-4 right-4 flex-row gap-4'>
          <Button
            type='default'
            icon={<ClockCircleOutlined />}
            className='text-sm font-lato'
            size='middle'
            onClick={() => {
              setMode(prev => (prev === 'timeline' ? 'general' : 'timeline'))
            }}
          >
            Timeline
          </Button>

          {checkRun?.report_totals && Object.keys(checkRun?.report_totals).length > 0 && (
            <Button
              type='default'
              icon={<DollarCircleOutlined />}
              className='text-sm font-lato'
              size='middle'
              onClick={() => {
                setMode(prev => (prev === 'totals' ? 'general' : 'totals'))
              }}
            >
              Check Registers
            </Button>
          )}
        </div>

        <div className='flex flex-col gap-4'>
          <div className='flex flex-row gap-4 items-start'>
            <div className='flex flex-col gap-4 min-w-72'>
              <Totals
                lcr_file={checkRun?.lcr_file ?? null}
                lcrTotal={lcrTotal}
                luminxTotal={luminxTotal}
                payment_files={checkRun?.payment_files ?? []}
                isLoading={query.isLoading}
                hasData={checkRun?.has_data ?? false}
              />

              {hasAccess([Role.Administrator]) && (
                <div className='flex flex-col gap-1 items-center'>
                  <div className='flex justify-center items-center opacity-0 transition-opacity hover:opacity-100'>
                    <Tooltip title={showCopiedTooltip ? 'Copied!' : ''} open={showCopiedTooltip}>
                      <button
                        type='button'
                        onClick={handleCopyCheckRunId}
                        className='font-mono text-xs text-gray-500 bg-transparent border-none cursor-pointer select-none'
                        aria-label='Copy Check Run ID'
                      >
                        {checkRunId}
                      </button>
                    </Tooltip>
                  </div>

                  {checkRun?.temporal_workflow_id && (
                    <div className='flex justify-center items-center gap-1 opacity-0 transition-opacity hover:opacity-100'>
                      <Tooltip title='Go to Temporal Overview'>
                        <button
                          type='button'
                          onClick={handleTemporalNavigate}
                          className='font-mono text-[10px] text-gray-400 bg-transparent border-none cursor-pointer select-none hover:text-blue-500 hover:underline'
                          aria-label='Go to Temporal Overview'
                        >
                          {checkRun.temporal_workflow_id}
                        </button>
                      </Tooltip>
                      <Tooltip
                        title={showTemporalCopiedTooltip ? 'Copied!' : 'Copy Workflow ID'}
                        open={showTemporalCopiedTooltip || undefined}
                      >
                        <Button
                          type='text'
                          size='small'
                          className='p-0 h-auto min-w-0 text-gray-400 hover:text-gray-600'
                          icon={<CopyOutlined style={{ fontSize: '10px' }} />}
                          onClick={e => {
                            e.stopPropagation()
                            handleCopyTemporalId()
                          }}
                        />
                      </Tooltip>
                    </div>
                  )}
                </div>
              )}

              {!showSettings && (
                <div className='flex justify-end items-center p-1 -m-1 -mt-2 opacity-0 transition-opacity hover:opacity-100'>
                  <button
                    className='flex justify-center items-center p-1 -m-1 text-xs text-gray-500 font-lato'
                    onClick={() => {
                      setShowSettings(true)
                    }}
                  >
                    <SettingOutlined className='mr-1' /> Show settings{' '}
                    <CaretDownFilled className='ml-1 text-gray-400 text-[0.65rem]' />
                  </button>
                </div>
              )}

              {showSettings && (
                <div className='relative'>
                  {settings.length > 0 && (
                    <div className='grid grid-cols-[max-content_1fr] gap-y-1 gap-x-4 p-4 bg-lucent-light-gray rounded-xl relative'>
                      {settings.map(setting => (
                        <Fragment key={setting.label}>
                          <span className='font-lato text-[0.625rem] uppercase font-semibold text-gray-500 text-left'>
                            {setting.label}
                          </span>

                          <span className='font-lato text-[0.625rem] uppercase font-semibold text-lucent-text text-left'>
                            {setting.value}
                          </span>
                        </Fragment>
                      ))}
                    </div>
                  )}

                  <div className='flex justify-start items-start mt-3'>
                    <div className='flex flex-col gap-1 w-full'>
                      <label
                        htmlFor='payment-method-display-type'
                        className='font-lato text-[0.625rem] uppercase font-semibold text-gray-500 text-left'
                      >
                        {paymentMethodDisplayType !== PaymentMethodDisplayType.Auto && (
                          <Tooltip title='This setting is manually set. Payment method buckets are overridden.'>
                            <WarningFilled className='text-orange-400 text-xs mr-1 -translate-y-[2px]' />
                          </Tooltip>
                        )}
                        Payment method buckets
                      </label>

                      <Select
                        id='payment-method-display-type'
                        value={paymentMethodDisplayType}
                        onChange={value => {
                          setPaymentMethodDisplayType(value)
                        }}
                        options={[
                          {
                            label: 'Auto',
                            value: PaymentMethodDisplayType.Auto
                          },
                          {
                            label: 'Last Check Register',
                            value: PaymentMethodDisplayType.LastReport
                          },
                          {
                            label: 'Current data',
                            value: PaymentMethodDisplayType.Claims
                          }
                        ]}
                        dropdownMatchSelectWidth={false}
                        size='small'
                        className='w-full text-left'
                      />
                    </div>
                  </div>

                  <button
                    type='button'
                    className='flex absolute top-3 right-3 justify-center items-center p-1 -m-1 text-xs text-gray-400 font-lato'
                    onClick={() => {
                      setShowSettings(false)
                    }}
                  >
                    <CloseOutlined />
                  </button>
                </div>
              )}
            </div>

            <div className='min-w-72'>
              {query.isLoading ? (
                <Skeleton.Node style={{ width: 288, height: 330, borderRadius: 16 }} />
              ) : (
                <Card
                  title='Total'
                  hasData={checkRun?.has_data ?? false}
                  error={{
                    value: lucentLCRDiff,
                    showError: !!lucentLCRDiff
                  }}
                  value={lucentpmt}
                  details={[
                    {
                      title: 'Lucent Net',
                      value: zelisColumn.some(c => !c.isEmpty)
                        ? zelisColumn.reduce(
                            (acc, item) => (item.label.endsWith('Net') ? acc + item.amount : acc),
                            0
                          )
                        : null,
                      data: zelisColumn
                        .filter(v => v.label.endsWith('Net'))
                        ?.map(v => ({
                          title: v.label,
                          value: v.amount,
                          tooltip: v.tooltip,
                          forceNumber: !v.isEmpty
                        }))
                    },
                    {
                      title: 'Lucent Voided',
                      value: zelisColumn.some(c => !c.isEmpty)
                        ? zelisColumn.reduce(
                            (acc, item) =>
                              item.label.endsWith('Voided') ? acc + item.amount : acc,
                            0
                          )
                        : null,
                      data: zelisColumn
                        .filter(v => v.label.endsWith('Voided'))
                        ?.map(v => ({
                          title: v.label,
                          value: Math.abs(v.amount),
                          tooltip: v.tooltip,
                          forceNumber: !v.isEmpty
                        }))
                    },
                    ...(checkRun?.is_network
                      ? [
                          {
                            title: 'Network',
                            value: networkTotal,
                            data: networkColumn?.map(v => ({
                              title: v.label,
                              value: v.amount,
                              tooltip: v.tooltip
                            }))
                          }
                        ]
                      : [])
                  ]}
                />
              )}
            </div>
          </div>

          {duplicateClaims.length > 0 && (
            <div className='flex flex-col gap-2'>
              {duplicateClaims.map(claim => (
                <Alert
                  key={claim.id}
                  type='info'
                  showIcon
                  className={'border-none py-4 px-6'}
                  icon={<Duplicate />}
                  message={
                    <span className='text-sm font-lato ml-2'>
                      Claim {claim.id} has multiple patients: {claim.patient_names}
                    </span>
                  }
                />
              ))}
            </div>
          )}
        </div>

        {fileGroups.length === 0 && query.isLoading && (
          <Skeleton paragraph={{ rows: 8 }} style={{ width: 300, height: 100 }} />
        )}

        {fileGroups.length > 0 && !query.isLoading && (
          <div className='flex flex-col flex-wrap gap-4 py-4 pl-6 h-full'>
            {fileGroups.map((group, i) => (
              <div key={i} className='flex flex-col gap-0.5 justify-start items-start'>
                <span className='pl-[22px] font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray'>
                  {group.name}
                </span>

                <div className='flex flex-col gap-2'>
                  {group.files.map(file => (
                    <div className='flex gap-2' key={file.href}>
                      {Object.keys(AVAILABLE_FILE_TYPES_MAPPER).includes(file.type) && (
                        <button
                          className='flex justify-center items-center'
                          onClick={handlePdfPreviewClick({
                            mimeType: AVAILABLE_FILE_TYPES_MAPPER[file.type],
                            name: file.name,
                            href: file.href
                          })}
                        >
                          <SearchOutlined />
                        </button>
                      )}

                      <a href={file.href} className='flex flex-row gap-2 items-center'>
                        {file.icon && (
                          <file.icon.component
                            className={cx(file.icon.className, 'text-sm -translate-y-[1px]')}
                          />
                        )}

                        {file.type === 'nomi_status_file' && file.sourceFilesDetails ? (
                          <NomiStatusFileTooltip sourceFilesDetails={file.sourceFilesDetails}>
                            <span
                              className={cx(
                                'font-lato text-sm text-ellipsis text-lucent-text text-left',
                                !file.icon && 'pl-[22px]'
                              )}
                            >
                              {file.name}
                            </span>
                          </NomiStatusFileTooltip>
                        ) : (
                          <span
                            className={cx(
                              'font-lato text-sm text-ellipsis text-lucent-text text-left',
                              !file.icon && 'pl-[22px]'
                            )}
                          >
                            {file.name}
                          </span>
                        )}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {canDisplayAllFiles && (
              <button
                type='button'
                className='flex flex-row gap-2 items-center text-sm font-lato text-lucent-gray'
                onClick={() => {
                  setDisplayAllFiles(!displayAllFiles)
                }}
              >
                <span className='text-xs uppercase'>
                  {displayAllFiles ? 'Show Less' : 'Show All'}
                </span>

                <CaretDownOutlined
                  className={cx(
                    'text-lucent-gray text-xs',
                    !displayAllFiles && '-translate-y-[1px]',
                    displayAllFiles && 'rotate-180 translate-y-[1px]'
                  )}
                />
              </button>
            )}
          </div>
        )}
      </div>

      {checkRun?.report_totals && mode === 'totals' && (
        <CheckRegisters
          warning={checkRun?.warning}
          reportTotals={checkRun?.report_totals}
          hasData={checkRun?.has_data ?? false}
        />
      )}

      {mode === 'timeline' && (
        <div className='pt-1'>
          <div className='w-[calc(100%_-_4rem)] h-[1px] bg-[#E6E7EB] mb-8 mx-auto' />

          <Timeline
            id={checkRunId}
            onClose={() => {
              setMode('general')
            }}
          />
        </div>
      )}
    </div>
  )
}
